/**
 * The window of page numbers to render in the numeric pagination footer.
 *
 * Always starts at 1 and ends at `pageCount`. Pages adjacent to the current
 * one are always included so a user can step one at a time. The window
 * collapses to ellipses on each side once `pageCount` exceeds the slot budget.
 */
export type PageWindow = {
  /** The pages to render as buttons, in order. */
  pages: number[];
  /** Where to insert a leading ellipsis. */
  hasLeadingEllipsis: boolean;
  /** Where to insert a trailing ellipsis. */
  hasTrailingEllipsis: boolean;
};

/**
 * Computes the page numbers to show given a current page and a total page count.
 *
 * The slot budget covers the current page, one slot either side, and the two
 * ends, with ellipses filling any gap. Examples for `pageCount = 15`:
 *
 *   current = 1   ->  [1, 2, 3, …, 15]
 *   current = 4   ->  [1, 2, 3, 4, 5, …, 15]
 *   current = 8   ->  [1, …, 7, 8, 9, …, 15]
 *   current = 15  ->  [1, …, 13, 14, 15]
 *
 * The numbers of slots are deliberately odd (7): a centered current page with
 * one neighbour each side reads as a single group, and the first / last slots
 * are taken by the ends.
 */
export function usePagination(current: number, pageCount: number, slotBudget = 7): PageWindow {
  if (pageCount <= 0) {
    return { pages: [], hasLeadingEllipsis: false, hasTrailingEllipsis: false };
  }

  // Everything fits — no ellipses needed.
  if (pageCount <= slotBudget) {
    return {
      pages: range(1, pageCount),
      hasLeadingEllipsis: false,
      hasTrailingEllipsis: false,
    };
  }

  const half = Math.floor(slotBudget / 2);
  const start = Math.max(1, current - half);
  const end = Math.min(pageCount, start + slotBudget - 1);

  // If we ran out of room at the top, pull `start` back so the window keeps
  // its full size — only the lower end gets an ellipsis.
  const adjustedStart = Math.max(1, end - slotBudget + 1);

  const pages = range(adjustedStart, end);

  return {
    pages,
    hasLeadingEllipsis: adjustedStart > 1,
    hasTrailingEllipsis: end < pageCount,
  };
}

/** Inclusive integer range. Returns an empty array when `from > to`. */
function range(from: number, to: number): number[] {
  if (from > to) return [];
  const length = to - from + 1;
  const result = new Array<number>(length);
  for (let index = 0; index < length; index += 1) {
    result[index] = from + index;
  }
  return result;
}
